import { css } from '@emotion/react';
import { theme } from '@frontend/theme';

export const GRID_AREAS = {
  CONTENT: 'content',
  META: 'meta',
  AVATAR: 'avatar',
  EMPTY: 'empty',
} as const;

const containerStyles = ({ direction }) => {
  const gridRowTemplate1 =
    direction === 'outbound'
      ? `${GRID_AREAS.CONTENT} ${GRID_AREAS.AVATAR}`
      : `${GRID_AREAS.AVATAR} ${GRID_AREAS.CONTENT}`;
  const gridRowTemplate2 =
    direction === 'outbound' ? `${GRID_AREAS.META} ${GRID_AREAS.EMPTY}` : `${GRID_AREAS.EMPTY} ${GRID_AREAS.META}`;

  return css`
    width: 100%;
    display: grid;
    grid-template-rows: 1fr auto;
    grid-template-columns: ${direction === 'inbound' ? 'auto 1fr' : '1fr auto'};
    padding: 0;
    gap: ${theme.spacing(0.5, 1)};
    grid-template-areas:
      '${gridRowTemplate1}'
      '${gridRowTemplate2}';
    align-items: end;
    margin-top: ${theme.spacing(1)};
  `;
};

const contentStyle = ({ direction }) => {
  return css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: ${direction === 'inbound' ? 'start' : 'end'};
    gap: ${theme.spacing(1)};
    position: relative;
    grid-area: ${GRID_AREAS.CONTENT};

    .bubbles-container {
      display: flex;
      flex-direction: column;
      max-width: min(95%, 600px);
      gap: ${theme.spacing(1)};
      align-items: ${direction === 'inbound' ? 'start' : 'end'};
    }
  `;
};

const metaInfoStyle = ({ direction }) => css`
  grid-area: ${GRID_AREAS.META};
  display: flex;
  flex-direction: column;
  align-items: ${direction === 'inbound' ? 'flex-start' : 'flex-end'};

  .tags-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    padding-bottom: ${theme.spacing(0.5)};
    justify-content: ${direction === 'inbound' ? 'flex-start' : 'flex-end'};
    align-items: ${direction === 'inbound' ? 'flex-start' : 'flex-end'};
    gap: ${theme.spacing(1)};
  }

  .meta-info-container {
    display: flex;
    gap: ${theme.spacing(1)};
    justify-content: ${direction === 'outbound' ? 'flex-end' : 'flex-start'};
    flex-direction: ${direction === 'inbound' ? 'row-reverse' : 'row'};
    padding-bottom: ${theme.spacing(0.5)};
  }

  .metadata-container {
    display: flex;
    gap: ${theme.spacing(1)};
  }
`;

const actionsStyle = css`
  border: 1px solid ${theme.colors.neutral20};
  border-radius: ${theme.borderRadius.small};
  box-shadow: ${theme.shadows.light};
  position: absolute;
  top: ${theme.spacing(-2)};
  background-color: ${theme.colors.white};
  display: flex;
  z-index: ${theme.zIndex.popover};
  padding: ${theme.spacing(0.25)};

  & button {
    padding: ${theme.spacing(0.5)};
    height: unset;
  }
`;

const bubbleStyles = ({ direction, isImage, isPdf, isVideo, backgroundColor, maxWidth, maxHeight }) => css`
  list-style-type: none;
  padding: ${theme.spacing(isImage || isVideo ? 0 : isPdf ? 1 : 2)};
  display: ${isImage || isVideo ? 'list-item' : 'flex'};
  overflow: ${isImage || isPdf || isVideo ? 'hidden' : 'visible'};
  gap: ${theme.spacing(2)};
  border-radius: ${theme.borderRadius.medium};
  background-color: ${backgroundColor ?? (direction === 'inbound' ? theme.colors.neutral5 : theme.colors.primary5)};
  white-space: pre-line;
  width: fit-content;
  max-height: ${maxHeight ?? (isImage || isVideo ? 'min(40vh, 480px)' : '100%')};
  max-width: ${maxWidth ?? (isImage || isVideo ? 'min(40%, 320px)' : isPdf ? 'min(80%, 320px)' : '100%')};

  .img-bubble {
    width: 100%;
    min-height: 150px;
    background-color: ${theme.colors.neutral5};
    border-radius: ${theme.borderRadius.medium};
  }

  .video-bubble-wrapper {
    position: relative;
    display: block;
    width: 100%;
    max-height: inherit;
    border-radius: ${theme.borderRadius.medium};
    overflow: hidden;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary50};
      outline-offset: 2px;
    }
  }

  .video-bubble-video {
    display: block;
    width: 100%;
    height: auto;
    max-height: inherit;
  }

  .video-bubble-play {
    position: absolute;
    left: ${theme.spacing(1.5)};
    bottom: ${theme.spacing(1.5)};
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.55);
    color: ${theme.colors.white};
    border: none;
    padding: 0;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover,
    &:focus-visible {
      background-color: rgba(0, 0, 0, 0.75);
    }

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary50};
      outline-offset: 2px;
    }
  }

  .pdf-bubble-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${theme.spacing(1)};
  }

  .pdf-bubble-preview {
    position: relative;
    width: 100%;
    /* US Letter aspect ratio so a single page fills the box without showing the next */
    aspect-ratio: 8.5 / 11;
    max-height: 320px;
    background-color: ${theme.colors.white};
    overflow: hidden;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary50};
      outline-offset: 2px;
    }
  }

  .pdf-bubble-preview-object {
    /* Oversize and offset to crop out the PDF viewer's gray gutter on all sides */
    position: absolute;
    top: -4%;
    left: -4%;
    width: 108%;
    height: 108%;
    border: none;
    pointer-events: none;
    background-color: ${theme.colors.white};
  }

  .pdf-bubble-preview-overlay {
    position: absolute;
    inset: 0;
    background: transparent;
  }

  .pdf-bubble {
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};
    padding: ${theme.spacing(1)};
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    background: transparent;
    border: none;
    border-radius: ${theme.borderRadius.small};
    cursor: pointer;
    text-align: left;

    &:hover,
    &:focus-visible {
      background-color: ${direction === 'inbound' ? theme.colors.neutral10 : theme.colors.primary10};
    }

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary50};
      outline-offset: 2px;
    }
  }

  .pdf-bubble-icon {
    position: relative;
    flex-shrink: 0;
    width: 32px;
    height: 40px;
    color: ${theme.colors.white};
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
  }

  .pdf-bubble-icon-shape {
    position: absolute;
    inset: 0;
    display: block;
  }

  .pdf-bubble-badge {
    position: absolute;
    top: 60%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.5px;
    color: ${theme.colors.primary50};
    pointer-events: none;
  }

  .pdf-bubble-info {
    flex: 1 0 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  }

  .pdf-bubble-filename {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0;
  }

  .pdf-bubble-filesize {
    margin: 0;
  }
`;

const previewModalStyles = css`
  padding: 0;
  max-width: unset;
  overflow: visible;

  .wv-modal-flex-content {
    padding: 0;
    max-height: 78vh;
  }

  img {
    max-height: inherit;
  }

  .btn-containers {
    position: absolute;
    display: flex;
    gap: ${theme.spacing(1)};
    top: ${theme.spacing(-4)};
    right: 0;
  }

  .download-button {
    background-color: ${theme.colors.white};
    height: ${theme.spacing(3)};
    width: ${theme.spacing(3)};
    padding: 0;
  }

  .close-button {
    background-color: ${theme.colors.white};
    height: ${theme.spacing(3)};
    width: ${theme.spacing(3)};
    padding: 0;
  }
`;

export const easeInOutVariants = {
  visible: { opacity: 1, transition: { duration: 0.3 } },
  hidden: { opacity: 0, transition: { duration: 0.3 } },
};

export const slideInVariants = {
  hidden: { height: 0, opacity: 0 },
  visible: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
};

export const ChatStyles = {
  container: containerStyles,
  content: contentStyle,
  meta: metaInfoStyle,
  actions: actionsStyle,
  bubble: bubbleStyles,
  previewModal: previewModalStyles,
};
